import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { rateMasterAPI, headcountAPI, customersAPI, projectsAPI } from "../api/client";
import { toast } from "react-toastify";
import "./RateMasterTab.css";
import { useMasters } from "../context/MastersContext";
import { mayPerformWithRole } from "../utils/userAccess";

const BILLING_RATE_TYPES = ["Hourly rate", "Daily rate", "Monthly rate"];
const CURRENCY_OPTIONS = ["INR", "USD", "CAD", "EUR", "GBP", "AED"];
const RATE_SCOPES = ["Common", "Customer", "Project"];

function cleanDesignation(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  const halfLen = Math.floor(trimmed.length / 2);
  if (trimmed.length % 2 === 0 && trimmed.slice(0, halfLen) === trimmed.slice(halfLen)) {
    return trimmed.slice(0, halfLen);
  }
  return trimmed;
}

function SearchableRateSelect({ value, options, onChange, placeholder, getOptionValue, getOptionLabel }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const selectedOption = options.find((option) => String(getOptionValue(option)) === String(value));
  const selectedLabel = selectedOption ? getOptionLabel(selectedOption) : String(value || "");
  const matchingOptions = options.filter((option) => getOptionLabel(option).toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  const optionListHeight = matchingOptions.length ? Math.min(matchingOptions.length, 7) * 36 : 40;

  const updateMenuPosition = useCallback(() => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    const menuHeight = Math.min(optionListHeight + 60, window.innerHeight - 24);
    const below = window.innerHeight - rect.bottom - 8;
    const above = rect.top - 8;
    const opensAbove = below < menuHeight && above > below;
    const availableHeight = Math.max(150, Math.min(menuHeight, opensAbove ? above - 4 : below));
    setMenuPosition({
      position: "fixed",
      top: opensAbove ? Math.max(8, rect.top - availableHeight - 4) : Math.min(rect.bottom + 4, window.innerHeight - availableHeight - 8),
      left: Math.max(8, Math.min(rect.left, window.innerWidth - rect.width - 8)),
      right: "auto",
      width: rect.width,
      maxHeight: availableHeight,
    });
  }, [optionListHeight]);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    updateMenuPosition();
    document.addEventListener("mousedown", handleOutsideClick);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    searchRef.current?.focus();
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  const choose = (option) => {
    onChange(getOptionValue(option));
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="rm-searchable-select" ref={rootRef}>
      <button type="button" className={`rm-searchable-trigger ${selectedLabel ? "" : "is-placeholder"}`} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((current) => !current)}>
        <span>{selectedLabel || placeholder}</span><span aria-hidden="true">▾</span>
      </button>
      {open && menuPosition && createPortal(
        <div className="rm-searchable-menu" ref={menuRef} style={menuPosition}>
          <input ref={searchRef} type="search" value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") event.preventDefault(); }} placeholder="Search..." aria-label={`Search ${placeholder}`} />
          <div className="rm-searchable-options" role="listbox" style={{ height: Math.min(optionListHeight, Math.max(40, menuPosition.maxHeight - 60)) }}>
            {matchingOptions.length ? matchingOptions.map((option) => (
              <button type="button" role="option" aria-selected={String(getOptionValue(option)) === String(value)} key={String(getOptionValue(option))} title={getOptionLabel(option)} onClick={() => choose(option)}>
                {getOptionLabel(option)}
              </button>
            )) : <div className="rm-searchable-empty">No matching options</div>}
          </div>
        </div>, document.body
      )}
    </div>
  );
}

const INITIAL_FORM = {
  designation: "",
  commercialModel: "",
  rateScope: "Common",
  customerId: "",
  projectId: "",
  billingRateType: "",
  currency: "INR",
  rate: "",
  isActive: true,
};

export default function RateMasterTab() {
  const { getList } = useMasters();
  const BILLING_RATE_TYPES = getList("billing_rate_type").filter((t) => t !== "Not Applicable");
  const CURRENCY_OPTIONS = getList("currency");

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const canEdit = mayPerformWithRole(user?.role, "resource_cost", "edit", "resource_cost.edit");

  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [formLoading, setFormLoading] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [designations, setDesignations] = useState([]);
  const [commercialModels, setCommercialModels] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    headcountAPI.getOptions().then((res) => {
      const rawDesignations = res.data.designations || [];
      setDesignations([...new Set(rawDesignations.map(cleanDesignation))].filter(Boolean));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    headcountAPI.getOptions().then((res) => {
      setCommercialModels(res.data.commercialModels || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    customersAPI.getAll().then((res) => {
      setCustomers(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    projectsAPI.getAll({ limit: 1000 }).then((res) => {
      setProjects(Array.isArray(res.data?.projects) ? res.data.projects : []);
    }).catch(() => {});
  }, []);

  const fetchRates = useCallback(async () => {
    setLoading(true);
    try {
      const params = { activeOnly: "false" };
      if (search) params.designation = search;
      if (typeFilter) params.billingRateType = typeFilter;
      if (currencyFilter) params.currency = currencyFilter;
      const res = await rateMasterAPI.getAll(params);
      setRates(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load rates:", err);
      toast.error("Failed to load rate master");
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, currencyFilter]);

  useEffect(() => { fetchRates(); }, [fetchRates]);

  const handleCreate = () => {
    setForm(INITIAL_FORM);
    setIsEditing(false);
    setShowForm(true);
  };

  const handleEdit = (rate) => {
    setForm({
      id: rate.id,
      designation: rate.designation || "",
      commercialModel: rate.commercial_model || "",
      rateScope: rate.rate_scope || (rate.customer_id ? "Customer" : "Common"),
      customerId: rate.customer_id || "",
      projectId: rate.project_id || "",
      billingRateType: rate.billing_rate_type || "",
      currency: rate.currency || "INR",
      rate: rate.rate || "",
      isActive: !!rate.is_active,
    });
    setIsEditing(true);
    setShowForm(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!form.designation || !form.billingRateType) {
      toast.error("Designation and Billing Rate Type are required");
      return;
    }
    if (!form.rate || Number(form.rate) < 0) {
      toast.error("A valid non-negative rate is required");
      return;
    }
    if (form.rateScope === "Customer" && !form.customerId) {
      toast.error("Select a Customer for Customer-scope rates");
      return;
    }
    if (form.rateScope === "Project" && !form.projectId) {
      toast.error("Select a Project for Project-scope rates");
      return;
    }
    setFormLoading(true);
    try {
      const payload = {
        designation: form.designation,
        commercialModel: form.commercialModel || null,
        rateScope: form.rateScope || "Common",
        customerId: form.rateScope === "Customer" ? form.customerId : null,
        projectId: form.rateScope === "Project" ? form.projectId : null,
        billingRateType: form.billingRateType,
        currency: form.currency,
        rate: Number(form.rate),
      };
      if (isEditing) {
        await rateMasterAPI.update(form.id, payload);
        toast.success("Rate updated");
      } else {
        await rateMasterAPI.create(payload);
        toast.success("Rate created");
      }
      setShowForm(false);
      await fetchRates();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save rate");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await rateMasterAPI.delete(deleteTarget.id);
      toast.success("Rate deactivated");
      setDeleteTarget(null);
      await fetchRates();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete");
    } finally {
      setDeleteLoading(false);
    }
  };

  const toggleStatus = async (id, isActive) => {
    try {
      await rateMasterAPI.update(id, { isActive });
      toast.success(isActive ? "Rate activated" : "Rate deactivated");
      await fetchRates();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update status");
    }
  };

  const filtered = rates.filter((r) => {
    if (!showInactive && !r.is_active) return false;
    if (statusFilter === "Active" && !r.is_active) return false;
    if (statusFilter === "Inactive" && r.is_active) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedRates = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  useEffect(() => { setCurrentPage(1); }, [search, typeFilter, currencyFilter, statusFilter, showInactive]);
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  return (
    <div className="rm-tab">

      <div className="rm-header">
        <div>
          <h2>Billing Rate Master</h2>
          <p>Manage role-based billing rates for project-level and customer-level rate cards</p>
        </div>
        <button type="button" className="rm-btn-primary" onClick={handleCreate} disabled={!canEdit}>
          + Add Rate
        </button>
      </div>

      {/* FILTERS */}
      <div className="rm-filters">
        <div className="rm-search-box">
          <input
            type="text"
            placeholder="Search by designation..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">All Rate Types</option>
          {BILLING_RATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={currencyFilter} onChange={(e) => setCurrencyFilter(e.target.value)}>
          <option value="">All Currencies</option>
          {CURRENCY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
        <button
          type="button"
          className={`rm-btn-toggle ${showInactive ? "active" : ""}`}
          onClick={() => setShowInactive(!showInactive)}
          title="Toggle inactive rate records"
        >
          {showInactive ? "Hide Inactive" : "Show Inactive"}
        </button>
      </div>

      {/* TABLE */}
      <div className="rm-table-wrapper">
        {loading ? (
          <div className="rm-loading">
            <div className="rm-spinner" />
            <p>Loading rates...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rm-empty">
            <div className="empty-icon">&#128176;</div>
            <h3>No billing rates found</h3>
            <p>{search || typeFilter || currencyFilter || statusFilter ? "Try adjusting your filters." : 'Click "+ Add Rate" to get started.'}</p>
          </div>
        ) : (
          <table className="rm-table">
            <thead>
              <tr>
                <th scope="col">Rate Scope</th>
                <th scope="col">Designation / Role</th>
                <th scope="col">Commercial Model</th>
                <th scope="col">Applies To (Customer / Project)</th>
                <th scope="col">Rate Type</th>
                <th scope="col">Currency</th>
                <th scope="col" style={{ textAlign: "right" }}>Rate</th>
                <th scope="col">Status</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRates.map((r) => {
                const matchedCustomer = customers.find((c) => c.customerId === r.customer_id);
                const matchedProject = projects.find((p) => p.project_id === r.project_id);
                const scope = r.rate_scope || (r.customer_id ? "Customer" : "Common");
                let appliesTo = "—";
                if (scope === "Customer") {
                  appliesTo = matchedCustomer ? `${matchedCustomer.customerId} - ${matchedCustomer.customerName}` : (r.customer_id || "—");
                } else if (scope === "Project") {
                  appliesTo = matchedProject ? `${matchedProject.project_id} - ${matchedProject.description || ""}`.trim() : (r.project_id || "—");
                }
                return (
                <tr key={r.id} className={!r.is_active ? "rm-inactive-row" : ""}>
                  <td><span className={`rm-scope-badge scope-${scope.toLowerCase()}`}>{scope}</span></td>
                  <td><strong>{r.designation}</strong></td>
                  <td>{r.commercial_model || "All Models"}</td>
                  <td>{appliesTo}</td>
                  <td>{r.billing_rate_type}</td>
                  <td>{r.currency}</td>
                  <td className="rm-num">{Number(r.rate).toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                  <td>
                    <span className={`rm-status-badge ${r.is_active ? "active" : "inactive"}`}>
                      {r.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="rm-actions">
                    {canEdit && <button type="button" className="rm-btn-icon rm-edit" title="Edit" onClick={() => handleEdit(r)}>&#9998;</button>}
                    {canEdit && (r.is_active ? (
                      <button type="button" className="rm-btn-status" title="Deactivate" onClick={() => toggleStatus(r.id, false)}>Deactivate</button>
                    ) : (
                      <button type="button" className="rm-btn-status rm-btn-activate" title="Activate" onClick={() => toggleStatus(r.id, true)}>Activate</button>
                    ))}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!loading && filtered.length > 0 && totalPages > 1 && (
        <div className="rate-pagination" aria-label="Billing Rate Master pagination">
          <span>Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filtered.length)} of {filtered.length}</span>
          <div className="rate-pagination-controls">
            <button type="button" onClick={() => setCurrentPage(safePage - 1)} disabled={safePage === 1}>Previous</button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
              <button type="button" key={pageNumber} className={pageNumber === safePage ? "active" : ""} onClick={() => setCurrentPage(pageNumber)}>{pageNumber}</button>
            ))}
            <button type="button" onClick={() => setCurrentPage(safePage + 1)} disabled={safePage === totalPages}>Next</button>
          </div>
        </div>
      )}

      {/* CREATE/EDIT MODAL */}
      {showForm && (
        <div className="rm-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="rm-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{isEditing ? "Edit Rate" : "Add Rate"}</h3>
            <form onSubmit={handleFormSubmit}>
              <div className="rm-modal-body">
                <div className="rm-form-group">
                  <label>Rate Scope *</label>
                  <select
                    value={form.rateScope}
                    onChange={(e) => setForm({
                      ...form,
                      rateScope: e.target.value,
                      ...(e.target.value === "Common" ? { customerId: "", projectId: "" } : {}),
                      ...(e.target.value === "Customer" ? { projectId: "" } : {}),
                      ...(e.target.value === "Project" ? { customerId: "" } : {}),
                    })}
                    required
                  >
                    {RATE_SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {form.rateScope === "Common" && (
                    <span className="rm-field-hint">Applies generally when there is no customer-specific or project-specific rate.</span>
                  )}
                </div>
                <div className="rm-form-group">
                  <label>Designation / Role *</label>
                  <SearchableRateSelect
                    value={form.designation}
                    options={designations}
                    getOptionValue={(designation) => designation}
                    getOptionLabel={(designation) => designation}
                    placeholder="Select designation"
                    onChange={(designation) => setForm({ ...form, designation })}
                  />
                </div>
                <div className="rm-form-group">
                  <label>Commercial Model (optional — leave blank for all models)</label>
                  <input
                    type="text"
                    value={form.commercialModel}
                    onChange={(e) => setForm({ ...form, commercialModel: e.target.value })}
                    placeholder="e.g. Time & Material (T&M)"
                    list="rm-commercial-models"
                  />
                  <datalist id="rm-commercial-models">
                    {getList("commercial_model").map((m) => <option key={m} value={m} />)}
                  </datalist>
                </div>
                {form.rateScope === "Customer" && (
                  <div className="rm-form-group">
                    <label>Customer *</label>
                    <SearchableRateSelect
                      value={form.customerId}
                      options={customers}
                      getOptionValue={(customer) => customer.customerId}
                      getOptionLabel={(customer) => `${customer.customerId} - ${customer.customerName}`}
                      placeholder="Select Customer"
                      onChange={(customerId) => setForm({ ...form, customerId })}
                    />
                  </div>
                )}
                {form.rateScope === "Project" && (
                  <div className="rm-form-group">
                    <label>Project *</label>
                    <SearchableRateSelect
                      value={form.projectId}
                      options={projects}
                      getOptionValue={(project) => project.project_id}
                      getOptionLabel={(project) => `${project.project_id} - ${project.description || project.end_customer || "Project"}`}
                      placeholder="Select Project"
                      onChange={(projectId) => setForm({ ...form, projectId })}
                    />
                  </div>
                )}
                <div className="rm-form-row">
                  <div className="rm-form-group">
                    <label>Billing Rate Type *</label>
                    <select value={form.billingRateType} onChange={(e) => setForm({ ...form, billingRateType: e.target.value })} required>
                      <option value="">Select</option>
                      {BILLING_RATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="rm-form-group">
                    <label>Currency *</label>
                    <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} required>
                      {CURRENCY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div className="rm-form-group">
                  <label>Rate *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.rate}
                    onChange={(e) => setForm({ ...form, rate: e.target.value })}
                    placeholder="Enter billing rate"
                    required
                  />
                </div>
              </div>
              <div className="rm-modal-actions">
                <button type="button" className="rm-btn-secondary" onClick={() => setShowForm(false)} disabled={formLoading}>Cancel</button>
                {isEditing && (
                  <button
                    type="button"
                    className={`rm-btn-status ${form.isActive ? "" : "rm-btn-activate"}`}
                    onClick={async () => {
                      const next = !form.isActive;
                      await toggleStatus(form.id, next);
                      setForm((prev) => ({ ...prev, isActive: next }));
                    }}
                    disabled={formLoading}
                  >
                    {form.isActive ? "Deactivate" : "Activate"}
                  </button>
                )}
                <button type="submit" className="rm-btn-primary" disabled={formLoading}>{formLoading ? "Saving..." : isEditing ? "Update" : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM MODAL */}
      {deleteTarget && (
        <div className="rm-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="rm-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Deactivate Rate</h3>
            <div className="rm-modal-body">
              <p>Deactivate the rate for <strong>{deleteTarget.designation}</strong> ({deleteTarget.billing_rate_type} / {deleteTarget.currency})?</p>
              <p>This rate will no longer be available for auto-fetch in new projects.</p>
            </div>
            <div className="rm-modal-actions">
              <button type="button" className="rm-btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>Cancel</button>
              <button type="button" className="rm-btn-danger" onClick={handleDelete} disabled={deleteLoading}>{deleteLoading ? "Deactivating..." : "Deactivate"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
